import {
  ReactNode,
  HTMLAttributes,
  useState,
  useRef,
  useEffect,
} from "react";
import clsx from "clsx";

/* ------------------------ Modal ------------------------ */

type ModalProps = {
  open: boolean;
  onClose: () => void;
  children?: ReactNode;
};

export const Modal = ({ open, onClose, children }: ModalProps) => {
  if (!open) return null;

  return (
    <div className="default_modal_overlay" onClick={onClose}>
      <div
        className="default_modal"
        onClick={(e) => e.stopPropagation()}
      >
        {children}
      </div>
    </div>
  );
};

/* ------------------------ Dropdown ------------------------ */

type DropdownProps = {
  trigger: ReactNode;
  children: ReactNode;
};

export const Dropdown = ({ trigger, children }: DropdownProps) => {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (!ref.current?.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  return (
    <div className="default_dropdown" ref={ref}>
      <div onClick={() => setOpen((p) => !p)}>{trigger}</div>
      {open && <div className="default_dropdown_menu">{children}</div>}
    </div>
  );
};

/* ------------------------ Accordion ------------------------ */

type AccordionItem = {
  title: ReactNode;
  content: ReactNode;
};

type AccordionProps = {
  items: AccordionItem[];
};

export const Accordion = ({ items }: AccordionProps) => {
  const [active, setActive] = useState<number | null>(null);

  return (
    <div className="default_accordion">
      {items.map((item, i) => (
        <div key={i} className="default_accordion_item">
          <button
            className="default_accordion_trigger"
            onClick={() => setActive(active === i ? null : i)}
          >
            {item.title}
          </button>
          {active === i && (
            <div className="default_accordion_content">
              {item.content}
            </div>
          )}
        </div>
      ))}
    </div>
  );
};

/* ------------------------ Tabs ------------------------ */

type TabItem = {
  label: ReactNode;
  content: ReactNode;
};

type TabsProps = {
  tabs: TabItem[];
};

export const Tabs = ({ tabs }: TabsProps) => {
  const [active, setActive] = useState(0);

  return (
    <div className="default_tabs">
      <div className="default_tabs_list">
        {tabs.map((tab, i) => (
          <button
            key={i}
            className={clsx(
              "default_tab_trigger",
              active === i && "active"
            )}
            onClick={() => setActive(i)}
          >
            {tab.label}
          </button>
        ))}
      </div>

      <div className="default_tab_content">
        {tabs[active].content}
      </div>
    </div>
  );
};

/* ------------------------ Tooltip ------------------------ */

type TooltipProps = {
  text: ReactNode;
  children: ReactNode;
};

export const Tooltip = ({ text, children }: TooltipProps) => (
  <div className="default_tooltip">
    {children}
    <span className="default_tooltip_text">{text}</span>
  </div>
);

/* ------------------------ Badge ------------------------ */

export const Badge = ({
  children,
  className = "",
}: HTMLAttributes<HTMLSpanElement>) => (
  <span className={clsx("default_badge", className)}>{children}</span>
);

/* ------------------------ Avatar ------------------------ */

type AvatarProps = {
  src?: string;
  alt?: string;
  fallback?: ReactNode;
};

export const Avatar = ({ src, alt, fallback }: AvatarProps) => (
  <div className="default_avatar">
    {src ? <img src={src} alt={alt} /> : fallback}
  </div>
);

/* ------------------------ Alert ------------------------ */

type AlertProps = HTMLAttributes<HTMLDivElement> & {
  variant?: "success" | "error" | "warning" | "info";
};

export const Alert = ({
  children,
  className = "",
  variant = "info",
  ...props
}: AlertProps) => (
  <div
    className={clsx(
      "default_alert",
      `default_alert_${variant}`,
      className
    )}
    {...props}
  >
    {children}
  </div>
);

/* ------------------------ Skeleton ------------------------ */

export const Skeleton = ({
  className = "",
}: HTMLAttributes<HTMLDivElement>) => (
  <div className={clsx("default_skeleton", className)} />
);

/* ------------------------ Switch Toggle ------------------------ */

type SwitchProps = {
  checked?: boolean;
  onChange?: (val: boolean) => void;
};

export const Switch = ({ checked = false, onChange }: SwitchProps) => {
  const [enabled, setEnabled] = useState(checked);

  const toggle = () => {
    setEnabled(!enabled);
    onChange?.(!enabled);
  };

  return (
    <button
      onClick={toggle}
      className={clsx("default_switch", enabled && "active")}
    >
      <span className="default_switch_thumb" />
    </button>
  );
};

/* ------------------------ Progress Bar ------------------------ */

type ProgressProps = {
  value: number;
};

export const Progress = ({ value }: ProgressProps) => (
  <div className="default_progress">
    <div
      className="default_progress_bar"
      style={{ width: `${value}%` }}
    />
  </div>
);
